import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Zap, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from '@/context/ThemeContext';

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { href: '/',          label: 'Home'      },
    { href: '/pricing',   label: 'Pricing'   },
    { href: '/faq',       label: 'FAQ'       },
    { href: '/insurance', label: 'Insurance' },
    { href: '/contact',   label: 'Contact'   },
  ];

  const isActive = (path) => location.pathname === path;
  const isLightThemeSticky = scrolled && theme === 'light';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled
        ? 'bg-[#0a0a0f]/90 backdrop-blur-xl border-b border-white/[0.06]'
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">

          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <Zap className="h-5 w-5 text-accent" strokeWidth={1.5} />
            <span className={`font-outfit font-semibold text-base tracking-tight transition-colors duration-300 ${
              isLightThemeSticky ? 'text-white' : 'text-gray-900 dark:text-white/90'
            }`}>
              ShipTesla
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-[13px] font-medium transition-colors duration-300 ${
                  isLightThemeSticky
                    ? isActive(link.href) ? 'text-white' : 'text-white/70 hover:text-white'
                    : isActive(link.href) ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-white/45 hover:text-gray-900 dark:hover:text-white/80'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-md transition-colors ${
                isLightThemeSticky
                  ? 'text-white/70 hover:text-white'
                  : 'text-gray-600 dark:text-white/40 hover:text-gray-900 dark:hover:text-white/70'
              }`}
              title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            >
              {theme === 'dark'
                ? <Sun className="h-4 w-4" />
                : <Moon className="h-4 w-4" />
              }
            </button>
            <Link to="/quote">
              <Button
                className={`rounded-full px-6 py-2 text-[13px] font-medium transition-all duration-300 backdrop-blur-sm ${
                  isLightThemeSticky
                    ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30 hover:border-white/50'
                    : 'bg-gray-200 dark:bg-white/10 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-white/20 border border-gray-300 dark:border-white/10 hover:border-gray-400 dark:hover:border-white/25'
                }`}
                data-testid="nav-get-quote-btn"
              >
                Get Quote
              </Button>
            </Link>
          </div>

          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="nav-mobile-menu-btn"
          >
            {isOpen
              ? <X className={`h-5 w-5 ${isLightThemeSticky ? 'text-white' : 'text-gray-700 dark:text-white/60'}`} />
              : <Menu className={`h-5 w-5 ${isLightThemeSticky ? 'text-white' : 'text-gray-700 dark:text-white/60'}`} />
            }
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-white/[0.06]">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium py-2 ${
                    isLightThemeSticky
                      ? isActive(link.href) ? 'text-white' : 'text-white/70'
                      : isActive(link.href) ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-white/45'
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`nav-mobile-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center gap-3 mt-1">
                <button
                  onClick={toggleTheme}
                  className={`p-2 rounded-md transition-colors ${
                    isLightThemeSticky
                      ? 'text-white/70 hover:text-white'
                      : 'text-gray-600 dark:text-white/40 hover:text-gray-900 dark:hover:text-white/70'
                  }`}
                >
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <Link to="/quote" onClick={() => setIsOpen(false)} className="flex-1">
                  <Button
                    className={`w-full rounded-full ${
                      isLightThemeSticky
                        ? 'bg-white/20 text-white border border-white/30'
                        : 'bg-gray-200 dark:bg-white/10 text-gray-900 dark:text-white border border-gray-300 dark:border-white/10'
                    }`}
                    data-testid="nav-mobile-get-quote-btn"
                  >
                    Get Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
